import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import { UserFormData } from '../services/userService';
import { UserRole, UserStatus, ROLE_LABELS, getPrimaryRole } from '../types/user';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (user: UserFormData) => void;
  initialData?: UserFormData;
  mode: 'add' | 'edit';
}

const ALL_ROLES = Object.values(UserRole);

const UserForm: React.FC<UserFormProps> = ({ open, onClose, onSubmit, initialData, mode }) => {
  const [formData, setFormData] = useState<UserFormData>({
    id: initialData?.id,
    lastName: initialData?.lastName || '',
    firstName: initialData?.firstName || '',
    middleName: initialData?.middleName || '',
    email: initialData?.email || '',
    role: initialData?.role || UserRole.AUTHOR,
    roles: initialData?.roles?.length
      ? initialData.roles
      : [initialData?.role || UserRole.AUTHOR],
    status: initialData?.status || UserStatus.ACTIVE,
    password: initialData?.password || ''
  });

  useEffect(() => {
    if (initialData) {
      const roles = initialData.roles?.length
        ? initialData.roles
        : [initialData.role || UserRole.AUTHOR];
      setFormData({
        id: initialData.id,
        lastName: initialData.lastName || '',
        firstName: initialData.firstName || '',
        middleName: initialData.middleName || '',
        email: initialData.email || '',
        role: getPrimaryRole(roles),
        roles,
        status: initialData.status || UserStatus.ACTIVE,
        password: initialData.password || ''
      });
    } else {
      setFormData({
        lastName: '',
        firstName: '',
        middleName: '',
        email: '',
        role: UserRole.AUTHOR,
        roles: [UserRole.AUTHOR],
        status: UserStatus.ACTIVE,
        password: ''
      });
    }
  }, [initialData]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRolesChange = (e: SelectChangeEvent<UserRole[]>) => {
    const value = e.target.value;
    const roles = (typeof value === 'string' ? value.split(',') : value) as UserRole[];
    const normalized = roles.length > 0 ? roles : [UserRole.USER];
    setFormData(prev => ({
      ...prev,
      roles: normalized,
      role: getPrimaryRole(normalized),
    }));
  };

  const handleStatusChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.value as UserStatus,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const roles = formData.roles?.length ? formData.roles : [formData.role];
    onSubmit({
      ...formData,
      roles,
      role: getPrimaryRole(roles),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'add' ? 'Добавить пользователя' : 'Редактировать пользователя'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Фамилия"
                name="lastName"
                value={formData.lastName}
                onChange={handleTextChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Имя"
                name="firstName"
                value={formData.firstName}
                onChange={handleTextChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Отчество"
                name="middleName"
                value={formData.middleName}
                onChange={handleTextChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleTextChange}
                required
              />
            </Grid>
            {mode === 'add' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Пароль"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleTextChange}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Роли</InputLabel>
                <Select
                  multiple
                  name="roles"
                  value={formData.roles || []}
                  onChange={handleRolesChange}
                  input={<OutlinedInput label="Роли" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} size="small" label={ROLE_LABELS[value] || value} />
                      ))}
                    </Box>
                  )}
                >
                  {ALL_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleStatusChange}
                  label="Статус"
                >
                  <MenuItem value={UserStatus.ACTIVE}>Активен</MenuItem>
                  <MenuItem value={UserStatus.INACTIVE}>Неактивен</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained" color="primary">
            {mode === 'add' ? 'Добавить' : 'Сохранить'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;
